import React, { memo, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Paper } from '@mui/material';
import classnames from 'classnames';
import { useMeContext } from 'modules/me';
import { useScrollbarStyles } from 'modules/common/styles/useScrollbarStyles';
import { CopyrightFooter } from 'modules/common/components/CopyrightFooter';
import { useSidePanelContext } from 'modules/rootPage/SidePanelContext';
import { useSubstituteUser } from 'modules/rootPage/hooks/useSubstituteUser';
import { StartEndSettingsLanding } from './StartEndSettingsLanding';
import { SmartSuggestionLanding } from './SmartSuggestionLanding';
import { SettingsType } from './constant';
import { SettingsTabs } from './SettingsTabs';

type PageStyleProps = {
  pinned: boolean;
  isSubstituteUser: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 'unset',
    backgroundColor: theme.palette.background.default,
    flexGrow: 1,
    marginTop: ({ isSubstituteUser }: PageStyleProps) =>
      isSubstituteUser ? theme.spacing(4) : 'unset',
    overflowX: 'hidden',
    overflowY: 'auto',
    minHeight: '50%',
    display: 'flex',
    paddingLeft: ({ pinned }: PageStyleProps) => theme.spacing(pinned ? 30 : 8),
    flexDirection: 'column'
  },
  content: {
    width: '100%',
    height: '100%',
    background: theme.palette.information.pageBackground,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  }
}));

export const SettingsPage: React.FC = memo(() => {
  const classesScrollbar = useScrollbarStyles();
  const { isSubstituteUser } = useSubstituteUser();
  const { pinned = false } = useSidePanelContext();
  const classes = useStyles({
    pinned,
    isSubstituteUser
  });
  const me = useMeContext();
  const [settingPagetype, showSettingPageType] = useState<
    SettingsType | undefined
  >(
    me?.permissions.hasStartEndEditAccess
      ? SettingsType.START_END_TIME
      : me?.permissions.hasSmartSuggestionEditAccess
      ? SettingsType.SMART_SUGGESTION
      : undefined
  );
  const showPage = useMemo(() => {
    switch (settingPagetype) {
      case SettingsType.START_END_TIME:
        return <StartEndSettingsLanding />;
      case SettingsType.SMART_SUGGESTION:
        return <SmartSuggestionLanding />;
      default:
        return <></>;
    }
  }, [settingPagetype]);

  return (
    <Paper
      className={classnames(classes.root, classesScrollbar.scrollbar)}
      square
      elevation={0}
      data-testid="setting-content-page"
    >
      <SettingsTabs
        showSettingPageType={showSettingPageType}
        settingPagetype={settingPagetype}
      />
      <div className={classes.content}>
        {showPage}
        <CopyrightFooter />
      </div>
    </Paper>
  );
});

SettingsPage.displayName = 'SettingsPage';
